import React, { useState, useRef, useEffect } from "react";
import { XuiServerConfig } from "../../../core/models/xui";
import './HostMultiSelect.css';

interface HostMultiSelectProps {
	hosts: XuiServerConfig[];
	selectedIds: string[];
	onChange: (selectedIds: string[]) => void;
	disabled?: boolean;
	className?: string;
}

export const HostMultiSelect: React.FC<HostMultiSelectProps> = ({
	hosts,
	selectedIds,
	onChange,
	disabled = false,
	className = "",
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Close on outside click
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const getLabel = () => {
		if (selectedIds.length === 0) return "Все хосты";
		if (selectedIds.length === 1) {
			const host = hosts.find((h) => h.identity === selectedIds[0]);
			return host?.name || host?.identity || "Хост";
		}
		return `Выбрано: ${selectedIds.length}`;
	};

	const handleToggle = (identity: string) => {
		if (selectedIds.includes(identity)) {
			onChange(selectedIds.filter((id) => id !== identity));
		} else {
			onChange([...selectedIds, identity]);
		}
	};

	const handleSelectAll = () => {
		onChange(hosts.map((h) => h.identity));
	};

	const handleClearAll = () => {
		onChange([]);
	};

	return (
		<div
			ref={containerRef}
			className={`dropdown-container host-multi-select ${className} ${disabled ? "disabled" : ""}`}
		>
			<button
				type="button"
				className={`dropdown-trigger ${isOpen ? "open" : ""} ${selectedIds.length > 0 ? "has-value" : ""}`}
				onClick={() => !disabled && setIsOpen(!isOpen)}
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<div className="trigger-content">
					<span className="dropdown-value">{getLabel()}</span>
				</div>
				<div className="dropdown-icons">
					{selectedIds.length > 0 && (
						<button
							type="button"
							className="clear-button"
							onClick={(e) => {
								e.stopPropagation();
								onChange([]);
							}}
							aria-label="Clear selection"
						>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
								<path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</button>
					)}
					<span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>
						<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
							<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</span>
				</div>
			</button>

			{isOpen && (
				<div className="dropdown-menu">
					<div className="dropdown-search">
						<div className="dropdown-search-actions">
							<button type="button" className="btn-select-all" onClick={handleSelectAll}>
								Выбрать все
							</button>
							<button type="button" className="btn-clear-all" onClick={handleClearAll}>
								Очистить
							</button>
						</div>
					</div>
					<div className="options-container">
						<ul className="dropdown-options">
							{hosts.map((host) => (
								<li
									key={host.identity}
									className={`dropdown-option ${selectedIds.includes(host.identity) ? "selected" : ""}`}
									onClick={() => handleToggle(host.identity)}
									role="option"
									aria-selected={selectedIds.includes(host.identity)}
								>
									<input
										type="checkbox"
										checked={selectedIds.includes(host.identity)}
										onChange={() => handleToggle(host.identity)}
										className="option-checkbox"
									/>
									<span className="option-label">{host.name || host.identity}</span>
									{selectedIds.includes(host.identity) && (
										<span className="checkmark">
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
												<path d="M13 4L6 12L3 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										</span>
									)}
								</li>
							))}
							{hosts.length === 0 && (
								<div className="dropdown-empty">
									<p>Нет доступных хостов</p>
								</div>
							)}
						</ul>
					</div>
				</div>
			)}
		</div>
	);
};